/**
 * Evidence Upload Slots Component
 * 
 * Dynamic upload slots for client portal, generated from Evidence Checklist.
 * Features:
 * - Priority-ordered slots (Critical → High → Medium → Low)
 * - Color-coded badges
 * - Soft validation warnings for doc type mismatch
 * - Progress tracking
 * - "Other Documents" catch-all
 */

import React, { useState, useCallback, useEffect } from 'react';
import { 
  Upload, 
  CheckCircle, 
  AlertTriangle, 
  FileText, 
  Image, 
  File,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
  Info,
  Plus
} from 'lucide-react';
import api from '../utils/api';

interface UploadSlot {
  id: string;
  document_name: string;
  reason: string;
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  priority_color: string;
  priority_order: number;
  expected_doc_type: string | null;
  status: 'pending' | 'uploaded' | 'obtained';
  uploaded_file: {
    filename: string;
    status: string;
    detected_type: string | null;
  } | null;
  financial_impact: number | null;
}

interface EvidenceUploadSlotsProps {
  reportId: string;
  claimId?: string;
  draftId?: string;
  clientName?: string;
  onUploadComplete?: () => void;
  /** When set, fetch slots from the portal token endpoint and upload via the batch endpoint. */
  portalToken?: string;
  /**
   * Server verdict on whether every required document is in. When explicitly false and there
   * are no slots, we must not claim "All Documents Received".
   */
  documentsComplete?: boolean;
  /** Server document counts (same source as the portal header/pill); preferred over slot counting. */
  documentsReceived?: number | null;
  documentsTotal?: number | null;
}

interface SlotFile {
  file: File;
  uploading: boolean;
  progress: number;
  error: string | null;
  warning: string | null;
  uploadId: string | null;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export default function EvidenceUploadSlots({
  reportId,
  claimId,
  draftId,
  clientName,
  onUploadComplete,
  portalToken,
  documentsComplete,
  documentsReceived,
  documentsTotal
}: EvidenceUploadSlotsProps) {
  const [slots, setSlots] = useState<UploadSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slotFiles, setSlotFiles] = useState<Record<string, SlotFile>>({});
  const [otherFiles, setOtherFiles] = useState<File[]>([]);
  const [otherUploading, setOtherUploading] = useState(false);
  const [otherUploadError, setOtherUploadError] = useState<string | null>(null);
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());

  // Fetch upload slots
  useEffect(() => {
    fetchSlots();
  }, [reportId]);

  const fetchSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      const endpoint = portalToken
        ? `/api/v1/portal/${portalToken}/upload-slots`
        : `/api/v1/evidence/portal/${reportId}/upload-slots`;
      const result = await api.get(endpoint);
      setSlots(result.slots || []);

      // Auto-expand critical items
      const criticalIds = (result.slots || [])
        .filter((s: UploadSlot) => s.priority === 'CRITICAL')
        .map((s: UploadSlot) => s.id);
      setExpandedSlots(new Set(criticalIds));
    } catch (err: any) {
      setError(err.message || 'Failed to load document requirements');
    } finally {
      setLoading(false);
    }
  };

  const toggleSlot = (slotId: string) => {
    setExpandedSlots(prev => {
      const next = new Set(prev);
      if (next.has(slotId)) {
        next.delete(slotId);
      } else {
        next.add(slotId);
      }
      return next;
    });
  };

  const handleSlotFileSelect = async (slotId: string, file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setSlotFiles(prev => ({
        ...prev,
        [slotId]: { file, uploading: false, progress: 0, error: 'File too large (max 100MB)', warning: null, uploadId: null }
      }));
      return;
    }

    // Set file in state
    setSlotFiles(prev => ({
      ...prev,
      [slotId]: { file, uploading: true, progress: 10, error: null, warning: null, uploadId: null }
    }));

    // Upload the file
    try {
      if (portalToken) {
        const formData = new FormData();
        formData.append('files', file);
        formData.append('portal_token', portalToken);
        formData.append('evidence_item_id', slotId);
        formData.append('uploader_type', 'client');
        formData.append('claim_id', claimId || reportId);
        formData.append('draft_id', draftId || '');
        formData.append('client_name', clientName || '');

        setSlotFiles(prev => ({
          ...prev,
          [slotId]: { ...prev[slotId], progress: 50 }
        }));

        const result = await api.upload('/api/v1/upload-savefirst/unified/batch', formData);
        if (result.failed_uploads > 0 || result.uploads?.[0]?.error) {
          throw new Error(result.uploads?.[0]?.error || 'Upload failed');
        }

        setSlotFiles(prev => ({
          ...prev,
          [slotId]: {
            ...prev[slotId],
            uploading: false,
            progress: 100,
            uploadId: result.uploads?.[0]?.upload_id || null,
            error: null
          }
        }));
      } else {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('claim_id', claimId || reportId);
        formData.append('draft_id', draftId || '');
        formData.append('uploader_type', 'client');
        formData.append('client_name', clientName || '');
        formData.append('evidence_item_id', slotId);

        setSlotFiles(prev => ({
          ...prev,
          [slotId]: { ...prev[slotId], progress: 30 }
        }));

        const result = await api.upload('/api/v1/upload-savefirst/unified', formData);

        setSlotFiles(prev => ({
          ...prev,
          [slotId]: {
            ...prev[slotId],
            uploading: false,
            progress: 100,
            uploadId: result.upload_id,
            error: null
          }
        }));
      }

      // Refresh slots to get updated status
      setTimeout(() => {
        fetchSlots();
        onUploadComplete?.();
      }, 1000);

    } catch (err: any) {
      setSlotFiles(prev => ({
        ...prev,
        [slotId]: { ...prev[slotId], uploading: false, progress: 0, error: err.message || 'Upload failed' }
      }));
    }
  };

  const handleOtherFilesSelect = (files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files).filter(f => f.size <= MAX_FILE_SIZE);
    setOtherFiles(prev => [...prev, ...newFiles]);
  };

  const uploadOtherFiles = async () => {
    if (otherFiles.length === 0) return;

    setOtherUploading(true);
    setOtherUploadError(null);
    try {
      if (portalToken) {
        const formData = new FormData();
        for (const file of otherFiles) {
          formData.append('files', file);
        }
        formData.append('portal_token', portalToken);
        formData.append('uploader_type', 'client');
        formData.append('claim_id', claimId || reportId);
        formData.append('draft_id', draftId || '');
        formData.append('client_name', clientName || '');

        const result = await api.upload('/api/v1/upload-savefirst/unified/batch', formData);
        if (result.failed_uploads > 0) {
          const failed = result.uploads?.filter((u: any) => u.error) || [];
          const errMsg = failed.map((u: any) => `${u.filename}: ${u.error}`).join('; ');
          throw new Error(errMsg || ` ${result.failed_uploads} file(s) failed to upload`);
        }
        setOtherFiles([]);
      } else {
        for (const file of otherFiles) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('claim_id', claimId || reportId);
          formData.append('draft_id', draftId || '');
          formData.append('uploader_type', 'client');
          formData.append('client_name', clientName || '');

          await api.upload('/api/v1/upload-savefirst/unified', formData);
        }
        setOtherFiles([]);
      }
      onUploadComplete?.();
    } catch (err: any) {
      console.error('Upload failed:', err);
      setOtherUploadError(err.message || 'Upload failed');
    } finally {
      setOtherUploading(false);
    }
  };

  const removeOtherFile = (index: number) => {
    setOtherFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) {
      return <Image className="w-4 h-4" />;
    }
    return <FileText className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
        <span className="ml-3" style={{ color: 'var(--color-text-secondary)' }}>Loading document requirements...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
        {error}
      </div>
    );
  }

  if (slots.length === 0 && documentsComplete === false) {
    return (
      <div className="p-6 border rounded-lg text-center" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-border)' }}>
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Your team is preparing the list</h3>
        <p style={{ color: 'var(--color-text-secondary)' }}>Some required documents are still outstanding. We will show exactly what to upload here shortly.</p>
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <div className="p-6 border rounded-lg text-center" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
        <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-verified)' }} />
        <h3 className="text-lg font-semibold" style={{ color: 'var(--color-verified)' }}>All Documents Received</h3>
        <p style={{ color: 'var(--color-verified)' }}>No additional documents are needed at this time.</p>
      </div>
    );
  }

  // Header count: server numbers when the parent passes them, else count slots by status
  // (completed slots stay in the list, so they are included in the total).
  const headerTotal = documentsTotal != null ? documentsTotal : slots.length;
  const headerUploaded = documentsTotal != null
    ? Math.min(documentsReceived ?? 0, documentsTotal)
    : slots.filter(s => s.status === 'uploaded' || s.status === 'obtained').length;
  const headerPercent = headerTotal > 0 ? Math.round((headerUploaded / headerTotal) * 100) : 0;

  // Group slots by priority
  const groupedSlots = {
    CRITICAL: slots.filter(s => s.priority === 'CRITICAL'),
    HIGH: slots.filter(s => s.priority === 'HIGH'),
    MEDIUM: slots.filter(s => s.priority === 'MEDIUM'),
    LOW: slots.filter(s => s.priority === 'LOW')
  };

  return (
    <div className="space-y-6" data-testid="evidence-upload-slots">
      {/* Progress Header */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Documents Needed for Your Claim</h2>
          <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {headerUploaded} of {headerTotal} uploaded
          </span>
        </div>
        <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${headerPercent}%`, backgroundColor: 'var(--color-brand)' }}
          />
        </div>
        <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
          Upload the documents below to help us process your claim faster
        </p>
      </div>

      {/* Priority Sections */}
      {(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const).map(priority => {
        const prioritySlots = groupedSlots[priority];
        if (prioritySlots.length === 0) return null;

        const priorityLabels = {
          CRITICAL: { label: 'Critical', bg: 'var(--color-needs-human-tint)', border: 'var(--color-needs-human-tint-bd)', text: 'var(--color-needs-human-text)' },
          HIGH: { label: 'High Priority', bg: 'var(--color-needs-human-tint)', border: 'var(--color-needs-human-tint-bd)', text: 'var(--color-needs-human-text)' },
          MEDIUM: { label: 'Medium Priority', bg: 'var(--color-needs-human-tint)', border: 'var(--color-needs-human-tint-bd)', text: 'var(--color-needs-human-text)' },
          LOW: { label: 'Low Priority', bg: 'transparent', border: 'var(--color-verified)', text: 'var(--color-verified)' }
        };

        const style = priorityLabels[priority];

        return (
          <div key={priority} className="space-y-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: style.bg }}>
              <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: style.text }} />
              <span className="font-semibold" style={{ color: style.text }}>{style.label}</span>
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>({prioritySlots.length} items)</span>
            </div>

            {prioritySlots.map(slot => {
              const slotFile = slotFiles[slot.id];
              const isExpanded = expandedSlots.has(slot.id);
              const isCompleted = slot.status === 'obtained' || slot.status === 'uploaded';

              return (
                <div
                  key={slot.id}
                  className="rounded-xl border-2 overflow-hidden transition-all"
                  style={
                    isCompleted
                      ? { backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }
                      : { backgroundColor: 'var(--color-bg-surface)', borderColor: style.border }
                  }
                  data-testid={`upload-slot-${slot.id}`}
                >
                  {/* Slot Header */}
                  <button
                    onClick={() => toggleSlot(slot.id)}
                    className="w-full px-4 py-3 flex items-center justify-between transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                      ) : (
                        <FileText className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
                      )}
                      <div className="text-left">
                        <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{slot.document_name}</span>
                        {slot.uploaded_file && (
                          <span className="ml-2 text-sm" style={{ color: 'var(--color-verified)' }}>
                            ✓ {slot.uploaded_file.filename}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className="px-2 py-0.5 text-xs font-medium rounded-full"
                        style={{ backgroundColor: slot.priority_color + '20', color: slot.priority_color }}
                      >
                        {slot.priority}
                      </span>
                      {isExpanded ? <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} /> : <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />}
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                      {/* Reason */}
                      <div className="flex items-start gap-2 mt-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-needs-human-text)' }} />
                        <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{slot.reason}</p>
                      </div>

                      {/* Upload Area */}
                      {!isCompleted && (
                        <div className="mt-4">
                          {slotFile?.uploading ? (
                            <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                              <div className="flex items-center gap-3">
                                <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
                                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Uploading {slotFile.file.name}...</span>
                              </div>
                              <div className="h-2 rounded-full mt-2 overflow-hidden" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                                <div
                                  className="h-full transition-all"
                                  style={{ width: `${slotFile.progress}%`, backgroundColor: 'var(--color-brand)' }}
                                />
                              </div>
                            </div>
                          ) : slotFile?.error ? (
                            <div className="p-4 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                              <div className="flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                                <AlertTriangle className="w-4 h-4" />
                                <span className="text-sm">{slotFile.error}</span>
                              </div>
                              <label className="mt-2 inline-block cursor-pointer text-sm hover:underline" style={{ color: 'var(--color-needs-human-text)' }}>
                                Try again
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={(e) => e.target.files?.[0] && handleSlotFileSelect(slot.id, e.target.files[0])}
                                />
                              </label>
                            </div>
                          ) : (
                            <label className="block cursor-pointer">
                              <div className="border-2 border-dashed rounded-lg p-6 text-center transition-colors" style={{ borderColor: 'var(--color-border)' }}>
                                <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} />
                                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                                  Drop your <strong>{slot.document_name}</strong> here
                                </p>
                                <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>or click to browse</p>
                              </div>
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) => e.target.files?.[0] && handleSlotFileSelect(slot.id, e.target.files[0])}
                                data-testid={`file-input-${slot.id}`}
                              />
                            </label>
                          )}

                          {/* Validation Warning */}
                          {slotFile?.warning && (
                            <div className="mt-3 p-3 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                                <div>
                                  <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{slotFile.warning}</p>
                                  <button className="mt-2 text-sm font-medium hover:underline" style={{ color: 'var(--color-needs-human-text)' }}>
                                    Upload anyway
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Already Uploaded */}
                      {isCompleted && slot.uploaded_file && (
                        <div className="mt-3 p-3 rounded-lg flex items-center gap-3" style={{ backgroundColor: 'transparent' }}>
                          <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                          <div>
                            <p className="text-sm font-medium" style={{ color: 'var(--color-verified)' }}>{slot.uploaded_file.filename}</p>
                            <p className="text-xs" style={{ color: 'var(--color-verified)' }}>Uploaded successfully</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Other Documents Section */}
      <div className="rounded-xl border-2 overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="px-4 py-3 border-b" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-2">
            <Plus size={18} style={{ color: 'var(--color-text-secondary)' }} />
            <span className="font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Other Documents</span>
          </div>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>Have additional documents not listed above? Upload them here.</p>
        </div>

        <div className="p-4">
          {/* File List */}
          {otherFiles.length > 0 && (
            <div className="mb-4 space-y-2">
              {otherFiles.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <div className="flex items-center gap-2">
                    {getFileIcon(file.name)}
                    <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{file.name}</span>
                    <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
                  </div>
                  <button
                    onClick={() => removeOtherFile(idx)}
                    className="p-1 rounded"
                  >
                    <X className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                  </button>
                </div>
              ))}
              <button
                onClick={uploadOtherFiles}
                disabled={otherUploading}
                className="w-full py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {otherUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload {otherFiles.length} file{otherFiles.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
              {otherUploadError && (
                <div className="p-3 rounded-lg flex items-center gap-2" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-needs-human-text)' }} />
                  <span className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{otherUploadError}</span>
                </div>
              )}
            </div>
          )}

          {/* Drop Zone */}
          <label className="block cursor-pointer">
            <div className="border-2 border-dashed rounded-lg p-6 text-center transition-colors" style={{ borderColor: 'var(--color-border)' }}>
              <File className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} />
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Drop additional files here</p>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>PDF, Excel, CSV, Images (max 100MB each)</p>
            </div>
            <input
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleOtherFilesSelect(e.target.files)}
              data-testid="other-files-input"
            />
          </label>
        </div>
      </div>
    </div>
  );
}
